import React from "react";

/**
 * Shared risk-status semantics for the assessment/risk pages and vendor dashboard.
 *
 * A risk is COMPLETED when its remediation is finished (remediationStatus "Completed") or its
 * lifecycle status is closed/remediated. Everything else — including risks with no remediation
 * started yet — is ACTIVE. Risk pages show active risks by default; the status filter lets users
 * switch to completed or all.
 */
const COMPLETED_LIFECYCLE = ["closed", "remediated", "completed"];

export const isCompletedRisk = (risk) => {
  if (!risk) return false;
  if ((risk.remediationStatus || "").toLowerCase() === "completed") return true;
  return COMPLETED_LIFECYCLE.includes((risk.status || "").toLowerCase());
};

export const isActiveRisk = (risk) => !isCompletedRisk(risk);

export const countActiveRisks = (risks) => (risks || []).filter(isActiveRisk).length;

// Status filter contract: "active" is the default everywhere.
export const RISK_STATUS_FILTER_DEFAULT = "active";

export const applyRiskStatusFilter = (risks, filterValue) => {
  if (filterValue === "completed") return (risks || []).filter(isCompletedRisk);
  if (filterValue === "all") return risks || [];
  return (risks || []).filter(isActiveRisk); // "active" (default)
};

/** The status dropdown appended at the right end of each page's filter row. */
export const RiskStatusFilter = ({ value, onChange, className }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={className || "border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white"}
  >
    <option value="active">Active Risks</option>
    <option value="completed">Completed Risks</option>
    <option value="all">All Risks</option>
  </select>
);

/**
 * Sources dropdown for pages that fold in risks tagged with the module whose primary RiskType
 * differs (Ransomware / DarkWeb / AI pages; mirrors the Vendor dashboard's sources filter).
 * `sources` = distinct source names present; empty string value = All Sources.
 */
export const RiskSourcesFilter = ({ value, onChange, sources, className }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={className || "border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white"}
  >
    <option value="">All Sources</option>
    {(sources || []).map((s) => (
      <option key={s} value={s}>{s}</option>
    ))}
  </select>
);

/** Per-risk status badge. Colors mirror the remediation-status badge in AllRisks.js. */
export const RiskStatusBadge = ({ risk, className }) => {
  const label = risk?.remediationStatus || "Not Started";
  const palette =
    label === "Completed"    ? "text-green-600 bg-green-100 border-green-300" :
    label === "In Progress"  ? "text-blue-600 bg-blue-100 border-blue-300" :
    label === "Under Review" ? "text-purple-600 bg-purple-100 border-purple-300" :
    label === "Planned"      ? "text-yellow-600 bg-yellow-100 border-yellow-300" :
    label === "Deferred"     ? "text-gray-600 bg-gray-100 border-gray-300" :
                               "text-gray-600 bg-gray-100 border-gray-300";
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${palette} ${className || ""}`}>
      {label}
    </span>
  );
};
